<template>
    <div>
        <FullPageLoader :isLoading="fullpageLoading"/>

        <b-modal :active.sync="invoiceExportModalOpen" id="invoiceexportmodal" aria-modal :width="800" scroll="keep" :can-cancel="true" style="overflow: initial">
            <header class="modal-card-head">
                <p class="modal-card-title">Transacties exporteren</p>
            </header>

            <section class="modal-card-body" :style="{ borderRadius: '0', overflow: exportType === 'snelstart' ? 'auto' : 'initial' }">
                <b-field label="In welk format wil je de export maken?"></b-field>
                <b-field>
                    <b-select placeholder="Maak een keuze" v-model="exportType" expanded :disabled="loading">
                        <option value="planiteasy">Excel document (.xlsx)</option>
                        <option value="snelstart">SnelStart importbestand (.xlsx)</option>
                    </b-select>
                </b-field>

                <b-field label="Factuurdatum bereik">
                    <b-datepicker
                        placeholder="Klik om te selecteren"
                        v-model="dateRange"
                        :nearby-selectable-month-days="true"
                        :disabled="loading"
                        clearable
                        range
                    >
                        <b-button
                            label="Geen"
                            type="is-danger"
                            icon-left="times"
                            outlined
                            @click="dateRange = null"
                        />
                        <b-button
                            label="Huidige maand"
                            type="is-primary"
                            icon-left="calendar-alt"
                            outlined
                            @click="setDateRangeThisMonth"
                        />
                    </b-datepicker>
                </b-field>

                <!-- Transaction Type and BTW Percentages (available for all export types except SnelStart) -->
                <slide-up-down :active="exportType !== 'snelstart'" class="row">
                    <b-field label="Type"></b-field>
                    <b-field class="transactionType">
                        <b-radio v-model="transactionType" :native-value="'debit'" :disabled="loading">
                            Debit
                        </b-radio>
                        <b-radio class="marginleft10" v-model="transactionType" :native-value="'credit'" :disabled="loading">
                            Credit
                        </b-radio>
                        <b-radio class="marginleft10" v-model="transactionType" :native-value="'all'" :disabled="loading">
                            Alle transacties
                        </b-radio>
                    </b-field>

                    <br>

                    <b-field label="BTW percentages"></b-field>
                    <b-field class="btwPercentages">
                        <b-radio v-model="btwPercentage" :native-value="'21'" :disabled="loading">
                            21%
                        </b-radio>
                        <b-radio class="marginleft10" v-model="btwPercentage" :native-value="'0'" :disabled="loading">
                            0%
                        </b-radio>
                        <b-radio class="marginleft10" v-model="btwPercentage" :native-value="'all'" :disabled="loading">
                            Alle percentages
                        </b-radio>
                    </b-field>
                </slide-up-down>

                <br>

                <b-field label="Betaalstatus"></b-field>
                <b-field class="paid">
                    <b-radio v-model="paid" :native-value="true" :disabled="loading">
                        Betaalde transacties
                    </b-radio>
                    <b-radio class="marginleft10" v-model="paid" native-value="open" :disabled="loading">
                        Openstaande transacties
                    </b-radio>
                    <b-radio class="marginleft10" v-model="paid" :native-value="false" :disabled="loading">
                        Alle aangemaakte transacties
                    </b-radio>
                </b-field>

                <br>

                <b-field label="Betaalmethodes">
                    <b-select placeholder="Maak een keuze" v-model="paymentMethod" expanded :disabled="loading">
                        <option value="all">Alle betaalmethodes</option>
                        <option value="incasso">Incasso</option>
                        <option value="manual">Handmatige betalingen (overschrijvingen)</option>
                        <option value="manual_cash">Cash betalingen</option>
                    </b-select>
                </b-field>

                <br>

                <!-- SnelStart settings -->
                <slide-up-down :active="exportType === 'snelstart'" class="row">
                    <hr>

                    <h5>SnelStart velden</h5>

                    <p class="control">
                        Gebruik het importbestand om boekingen in SnelStart te importeren. In dit importbestand moeten een aantal verplichte kolommen zijn toegevoegd.&nbsp;
                        <a href="https://kennisplein.snelstart.nl/klanten/s/article/importeren-boekingen-importeren#deboekingenimporteren" target="_blank">Meer informatie</a>.
                    </p>

                    <div class="row">
                        <div class="column medium-12 large-4">
                            <b-field label="Dagboek *">
                                <b-input type="number" v-model="snelstart.fldDagboek" :disabled="loading"></b-input>
                            </b-field>
                        </div>
                        <div class="column medium-12 large-4">
                            <b-field label="Grootboeknummer *">
                                <b-input type="number" v-model="snelstart.fldGrootboeknummer" :disabled="loading"></b-input>
                            </b-field>
                        </div>
                        <div class="column medium-12 large-4">
                            <b-field label="Relatiecode *">
                                <b-input type="number" v-model="snelstart.fldRelatiecode" :disabled="loading"></b-input>
                            </b-field>
                        </div>
                    </div>
                </slide-up-down>
            </section>

            <footer class="modal-card-foot">
                <b-button type="is-light" @click="invoiceExportModalOpen = false" :disabled="loading">Sluiten</b-button>
                <b-button type="is-success" :loading="loading" @click="makeExport" :disabled="loading || !canExport">Exporteren</b-button>
            </footer>
        </b-modal>
    </div>
</template>

<script>
import excelExportingMixin from '@/mixins/excelExportMixin'

export default {
    name: 'InvoiceExportModal',

    mixins: [excelExportingMixin],

    props: {
        passDateRange: {
            type: Array|null,
            default: null,
            required: false
        }
    },

    data() {
        return {
            loading: false,
            fullpageLoading: false,
            invoiceExportModalOpen: false,
            paid: true,
            exportType: 'planiteasy',
            excelExportType: 'invoices',
            dateRange: null,
            transactionType: 'debit',
            btwPercentage: 'all',
            paymentMethod: 'all',

            // SnelStart settings
            snelstart: {
                fldDagboek: '',
                fldGrootboeknummer: '',
                fldRelatiecode: ''
            }
        }
    },

    computed: {
        user() {
            return this.$store.state.user
        },

        type() {
            return this.$route.params.type || this.$route.meta.type
        },

        canExport() {
            if (this.exportType === 'snelstart') {
                if (this.type === 'recent') {
                    return this.snelstart.fldGrootboeknummer && this.snelstart.fldRelatiecode && this.snelstart.fldDagboek
                } else {
                    return this.snelstart.fldGrootboeknummer && this.snelstart.fldRelatiecode && this.snelstart.fldDagboek
                }
            }

            // For non-SnelStart exports, ensure transaction type is selected
            return this.transactionType && this.transactionType !== ''
        },

        // Debug computed property to help troubleshoot
        debugInfo() {
            return {
                type: this.type,
                transactionType: this.transactionType,
                exportType: this.exportType
            }
        }
    },

    created() {
        if (!this.passDateRange) {
            this.setDateRangeThisMonth()
        }
    },

    mounted() {
        // Set default transaction type based on route after component is mounted
        this.setTransactionTypeFromRoute()
    },

    methods: {
        toggleExportModal() {
            if (this.passDateRange && this.passDateRange.length > 0) {
                this.dateRange = this.passDateRange
            }

            this.invoiceExportModalOpen = !this.invoiceExportModalOpen;
        },

        setDateRangeThisMonth(){
            // const thisMonth = [moment(new Date()).startOf('month')._d, moment(new Date()).endOf('month')._d]
            // this.$store.dispatch('setDateRange', thisMonth)
            this.dateRange = [moment(new Date()).startOf('month')._d, moment(new Date()).endOf('month')._d]
        },

        setTransactionTypeFromRoute() {
            // Set default transaction type based on route
            if (this.type === 'credit') {
                this.transactionType = 'credit'
            } else {
                this.transactionType = 'debit'
            }
        },

        makeExport() {
            this.loading = true

            if (this.exportType === 'snelstart') {
                const newSettings = !_.isNull(this.user.settings) ? this.user.settings : {}

                if (!_.has(newSettings, 'exports')) newSettings.exports = {} // add exports object if not exists
                newSettings.exports.snelstart = this.snelstart

                axios.post('/api/user/save', { settings: newSettings }, {
                    headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
                }).then((response) => {
                    this.$store.dispatch('saveUserSettings', response.data)
                    this.loading = false
                })
            }

            this.$store.commit('startExport', true)
        }
    },

    watch: {
        exportType(newVal) {
            // Set SnelStart settings if available
            if (newVal === 'snelstart') {
                // Set transaction type to 'all' for SnelStart
                this.transactionType = 'all'
                // Set BTW percentage to 'all' for SnelStart
                this.btwPercentage = 'all'

                if (_.has(this.user, 'settings.exports.snelstart')){
                    this.snelstart = this.user.settings.exports.snelstart
                }
            }
            if (newVal === 'planiteasy') {
                this.setTransactionTypeFromRoute()
            }
        }
    }
}
</script>

<style lang="scss">
    #invoiceexportmodal {
        .modal-content{overflow: inherit;}
    }
</style>
